import { Link } from 'react-router-dom';
import { MenuLinkData } from '@/lib/api';
import { useSiteMenus } from '@/hooks/useSiteMenus';
import { useSiteConfig } from '@/hooks/useSiteConfig';

// Fallbacks shown until the admin-managed menus load (and if the menus API is
// unreachable) — mirror the seeded defaults so there's no layout flash.
const FALLBACK_MENUS: Record<'footer_help' | 'footer_company' | 'footer_policies', MenuLinkData[]> = {
  footer_help: [
    { label: 'My Account', url: '/account', new_tab: false },
    { label: 'My Orders', url: '/orders', new_tab: false },
    { label: 'Contact Us', url: '/help', new_tab: false },
    { label: 'Bulk Orders', url: '/categories', new_tab: false },
  ],
  footer_company: [
    { label: 'About EsyPrints', url: '/about-us', new_tab: false },
    { label: 'Careers', url: '/careers', new_tab: false },
  ],
  footer_policies: [
    { label: 'Terms & Conditions', url: '/terms-and-conditions', new_tab: false },
    { label: 'Privacy Policy', url: '/privacy-policy', new_tab: false },
    { label: 'Returns & Refunds', url: '/returns-and-refunds', new_tab: false },
    { label: 'Shipping Policy', url: '/shipping-policy', new_tab: false },
  ],
};

// External http(s) URLs need an <a>; internal paths use the SPA <Link>.
function MenuItems({ links }: { links: MenuLinkData[] }) {
  return (
    <>
      {links.map((l) => (
        <li key={`${l.label}-${l.url}`}>
          {/^https?:\/\//i.test(l.url) || l.new_tab ? (
            <a href={l.url} target={l.new_tab ? '_blank' : undefined} rel="noopener">
              {l.label}
            </a>
          ) : (
            <Link to={l.url}>{l.label}</Link>
          )}
        </li>
      ))}
    </>
  );
}

export default function Footer() {
  // "Become a Service Provider" is admin-controlled (Settings → Feature
  // toggles). Hidden until the config confirms it should show, so switching
  // it off in the admin panel removes the option on the next page load.
  const siteConfig = useSiteConfig();
  // Footer link columns are admin-managed (Content → Site Menus); the shared
  // hook caches the fetch across Footer / MainNav / MobileNav.
  const siteMenus = useSiteMenus();
  const menus = {
    footer_help: siteMenus?.footer_help?.length ? siteMenus.footer_help : FALLBACK_MENUS.footer_help,
    footer_company: siteMenus?.footer_company?.length ? siteMenus.footer_company : FALLBACK_MENUS.footer_company,
    footer_policies: siteMenus?.footer_policies?.length ? siteMenus.footer_policies : FALLBACK_MENUS.footer_policies,
  };

  return (
    <footer className="site-footer">
      <div className="container-site">
        <div className="footer-grid">
          {/* Brand Column */}
          <div className="footer-brand">
            <Link to="/" className="logo" style={{ color: 'white' }}>
              Esy<span>Prints</span>
            </Link>
            <p>
              India&apos;s hyper-local on-demand print marketplace. Design, order, and get
              professionally printed products delivered to your doorstep in hours by local
              printers near you.
            </p>
            <div className="footer-social">
              {/* TODO: replace these /help fallbacks with real social URLs once
                  the marketing accounts are live. Using target="_blank" with
                  noopener so we're spec-correct in case the hrefs are
                  swapped to external links inline. */}
              <a href="/help" target="_blank" rel="noopener" aria-label="Facebook">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                  <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
                </svg>
              </a>
              <a href="/help" target="_blank" rel="noopener" aria-label="Twitter">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                  <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"/>
                </svg>
              </a>
              <a href="/help" target="_blank" rel="noopener" aria-label="Instagram">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="2" width="20" height="20" rx="5"/>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                </svg>
              </a>
              <a href="/help" target="_blank" rel="noopener" aria-label="LinkedIn">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
                  <rect x="2" y="9" width="4" height="12"/>
                  <circle cx="4" cy="4" r="2"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Link columns — admin-managed via Content → Site Menus */}
          <div className="footer-col">
            <h4>Let Us Help</h4>
            <ul>
              <MenuItems links={menus.footer_help} />
            </ul>
          </div>

          <div className="footer-col">
            <h4>Our Company</h4>
            <ul>
              <MenuItems links={menus.footer_company} />
              {siteConfig?.sp_registration_visible && (
                <li>
                  <a href={siteConfig.sp_register_url} target="_blank" rel="noopener">
                    Become a Service Provider
                  </a>
                </li>
              )}
            </ul>
          </div>

          <div className="footer-col">
            <h4>Our Policies</h4>
            <ul>
              <MenuItems links={menus.footer_policies} />
            </ul>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <span>&copy; {new Date().getFullYear()} EsyPrints. All rights reserved.</span>
          <div className="payment-icons">
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="28" viewBox="0 0 48 28" fill="none">
              <rect width="48" height="28" rx="4" fill="rgba(255,255,255,0.15)"/>
              <text x="24" y="18" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="10" fontFamily="sans-serif" fontWeight="700">VISA</text>
            </svg>
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="28" viewBox="0 0 48 28" fill="none">
              <rect width="48" height="28" rx="4" fill="rgba(255,255,255,0.15)"/>
              <circle cx="20" cy="14" r="8" fill="rgba(255,100,50,0.4)"/>
              <circle cx="28" cy="14" r="8" fill="rgba(255,200,0,0.4)"/>
            </svg>
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="28" viewBox="0 0 48 28" fill="none">
              <rect width="48" height="28" rx="4" fill="rgba(255,255,255,0.15)"/>
              <text x="24" y="18" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="10" fontFamily="sans-serif" fontWeight="700">UPI</text>
            </svg>
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="28" viewBox="0 0 48 28" fill="none">
              <rect width="48" height="28" rx="4" fill="rgba(255,255,255,0.15)"/>
              <text x="24" y="18" textAnchor="middle" fill="rgba(255,255,255,0.6)" fontSize="9" fontFamily="sans-serif" fontWeight="700">RuPay</text>
            </svg>
          </div>
        </div>
      </div>
    </footer>
  );
}
